import React, { useImperativeHandle, useRef } from "react";
import { SpreadOverviewHeader } from "./spread-overview-header";
import { scrollIntoView } from "../../utils/dom";

import "./spread-overview.css";
import { SpreadOverviewItemContainer } from "./spread-overview-item-container";
import { useSpreads } from "../../hooks/useSpreads";
import { useKeyDown, useKeyTrap } from "../../hooks/useKeyboard";
import { useFocusLock } from "../../hooks/useFocusLock";

export interface IVerticalScrollableRef {
	scrollIntoView(index: number, animate?: boolean): void;
}

const SpreadOverviewContent = ({
	onSelectSpreadIndex,
	selectedSpreadIndex,
	ref,
}: {
	selectedSpreadIndex: number | undefined;
	onSelectSpreadIndex: (index: number) => void;
	ref?: React.Ref<IVerticalScrollableRef | null>;
}) => {
	const spreads = useSpreads();

	const containerRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(
		ref,
		() => ({
			scrollIntoView: (index: number, animate = false) => {
				if (!containerRef.current) return;
				const selectedEl = containerRef.current.children[index];
				if (!selectedEl) return;

				scrollIntoView(selectedEl, {
					animate,
					// Top margin is greater so we can see a part of the previous spread
					margins: eXpress.math.margins(0, 120, 0, 40),
				});
			},
		}),
		[],
	);

	if (!spreads) return null;

	return (
		<div className="spread-overview_content" ref={containerRef}>
			{spreads.map((spread, index) => (
				<SpreadOverviewItemContainer
					key={spread.oid}
					spread={spread}
					isSelected={selectedSpreadIndex === index}
					onSelect={() => onSelectSpreadIndex(index)}
					canAddSpreads={spread.canInsertAfter()}
				/>
			))}
		</div>
	);
};

SpreadOverviewContent.displayName = "SpreadOverviewContent";

export const SpreadOverview = ({
	onClose,
	onSelectSpreadIndex,
	selectedSpreadIndex,
	onDeleteSelection,
	isDeleteEnabled,
	contentScrollRef,
}: {
	onClose: () => void;
	selectedSpreadIndex: number | undefined;
	onSelectSpreadIndex: (spreadIndex: number) => void;
	onDeleteSelection: () => void;
	isDeleteEnabled: boolean;
	contentScrollRef: React.RefObject<IVerticalScrollableRef | null>;
}) => {
	const ref = useRef<HTMLDivElement>(null);

	// Keep focus on this fullscreen overview
	useFocusLock(ref, {
		isActive: true,
		autoFocus: true,
		restoreFocus: true,
	});

	// Because fullscreen view is on top over work area, prevent key presses from bubbling up to main screen
	useKeyTrap(ref);

	// Close on Escape
	useKeyDown(
		(event) => {
			if (event.key === "Escape") {
				onClose();
				event.preventDefault();
			}
		},
		{
			ref, // Trap keys on the overview itself (or a child), not if something else has focus
		},
	);

	return (
		<div className="spread-overview" ref={ref}>
			<SpreadOverviewHeader
				onClose={onClose}
				isDeleteEnabled={isDeleteEnabled}
				onDelete={onDeleteSelection}
			/>
			<SpreadOverviewContent
				onSelectSpreadIndex={onSelectSpreadIndex}
				selectedSpreadIndex={selectedSpreadIndex}
				ref={contentScrollRef}
			/>
		</div>
	);
};
